// Tag detail view — compact single-shot report for a completed DV tag.
// Extracted from /app/frontend/src/pages/DiamondVerification.jsx.

import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, RotateCcw } from 'lucide-react';
import { API, token, LAB_COLORS, LABS, COLORS, CLARITIES, SOURCE_META } from './constants';
import { ColorPicker, ClarityPicker } from './common';
import { useAuth } from '../../context/AuthContext';

// Roles allowed to append a new lab proposal to an already-complete DV.
// Same set as _check_edit on the backend, minus roles that don't work with DV grades.
const CAN_ADD_LAB_ROLES = ['admin', 'owner', 'production_supervisor', 'diamond_dept'];
// Roles allowed to reopen a completed DV for a full revision (matches backend gate).
const CAN_REOPEN_ROLES = ['admin', 'owner'];

export const TagDetail = ({ tag: tagProp, onClose, onSaved, onReopen }) => {
  const { currentUser } = useAuth();
  const [tag, setTag] = useState(tagProp);
  const canAddLab = CAN_ADD_LAB_ROLES.includes(currentUser?.role);
  const canReopen = CAN_REOPEN_ROLES.includes(currentUser?.role);

  const entries   = tag.actual?.entries || [];
  const grossWt   = tag.actual?.gross_weight;

  // A diamond entry contributes to the "totals" the user sees at the top and
  // to every % ratio shown in the breakdown/entries table. Colour-stones,
  // silent/non-actionable rows are shown but do NOT get a percentage (their
  // weight is not part of the diamond denominator — matches Wizard.jsx totals).
  const isDiamondEntry = (e) =>
    e.actionable !== false && !e.is_silent && e.is_diamond !== false;

  const diamondEntries = entries.filter(isDiamondEntry);

  // Totals are re-derived from the diamond entries so stale stored totals
  // (e.g. edited-after-save data drift) can never surface as >100% ratios.
  const totalCts = diamondEntries.reduce((s, e) => s + (parseFloat(e.weight_cts) || 0), 0)
                    || tag.actual?.total_weight_cts || 0;
  const totalPcs = diamondEntries.reduce((s, e) => s + (parseInt(e.count) || 0), 0)
                    || tag.actual?.diamond_count || 0;

  const allActColors    = [...new Set(diamondEntries.flatMap(e => e.colors || []))].sort();
  const allActClarities = [...new Set(diamondEntries.flatMap(e => e.clarities || []))].sort();
  const tdsColors       = tag.tds?.colors || [];
  const tdsClarities    = tag.tds?.clarities || [];
  const colourMatch     = JSON.stringify(allActColors) === JSON.stringify([...tdsColors].sort());
  const clarityMatch    = JSON.stringify(allActClarities) === JSON.stringify([...tdsClarities].sort());

  const pct = (val, total) =>
    total > 0 && val != null ? `${((val / total) * 100).toFixed(1)}%` : null;

  const fmtCts = (val) => val != null ? parseFloat(val).toFixed(3) : null;

  // Breakdown by colour group — diamond entries only, skip empty-colour rows
  const colourBreakdown = Object.values(
    diamondEntries.reduce((acc, e) => {
      const key = (e.colors || []).join(', ');
      if (!key) return acc;
      if (!acc[key]) acc[key] = { label: key, cts: 0, pcs: 0 };
      acc[key].cts += parseFloat(e.weight_cts) || 0;
      acc[key].pcs += parseInt(e.count) || 0;
      return acc;
    }, {})
  );

  // Breakdown by clarity group — diamond entries only, skip empty-clarity rows
  const clarityBreakdown = Object.values(
    diamondEntries.reduce((acc, e) => {
      const key = (e.clarities || []).join(', ');
      if (!key) return acc;
      if (!acc[key]) acc[key] = { label: key, cts: 0, pcs: 0 };
      acc[key].cts += parseFloat(e.weight_cts) || 0;
      acc[key].pcs += parseInt(e.count) || 0;
      return acc;
    }, {})
  );

  const showBreakdown = diamondEntries.length > 0 && totalCts > 0 &&
    (colourBreakdown.length > 1 || clarityBreakdown.length > 1 ||
     diamondEntries.some(e => (e.colors || []).length > 1 || (e.clarities || []).length > 1));

  // ── Add-Lab modal state ─────────────────────────────────────────────
  // Only labs NOT already proposed can be added (dedupe by lab name).
  const existingLabNames = useMemo(
    () => new Set((tag.lab_proposals || []).map(p => p.lab)),
    [tag.lab_proposals],
  );
  const availableLabs = useMemo(
    () => LABS.filter(l => !existingLabNames.has(l)),
    [existingLabNames],
  );

  // Worst-actual grade — mirrors backend clamp so pickers disable "worse-than-actual" chips.
  const worstActualColor = useMemo(() => {
    const picked = diamondEntries.flatMap(e => e.colors || []);
    const valid = picked.filter(c => COLORS.includes(c));
    if (!valid.length) return null;
    return valid.reduce((w, c) => COLORS.indexOf(c) > COLORS.indexOf(w) ? c : w);
  }, [diamondEntries]);
  const worstActualClarity = useMemo(() => {
    const picked = diamondEntries.flatMap(e => e.clarities || []);
    const valid = picked.filter(c => CLARITIES.includes(c));
    if (!valid.length) return null;
    return valid.reduce((w, c) => CLARITIES.indexOf(c) > CLARITIES.indexOf(w) ? c : w);
  }, [diamondEntries]);

  const [modalOpen, setModalOpen]         = useState(false);
  const [newLab,    setNewLab]            = useState('');
  const [newColors, setNewColors]         = useState([]);
  const [newClarities, setNewClarities]   = useState([]);
  const [saving,    setSaving]            = useState(false);

  const openAddLab = () => {
    setNewLab(availableLabs[0] || '');
    setNewColors([]);
    setNewClarities([]);
    setModalOpen(true);
  };

  const saveAddLab = async () => {
    if (!newLab) { toast.error('Pick a lab'); return; }
    if (!newColors.length || !newClarities.length) {
      toast.error('Pick at least one colour and one clarity');
      return;
    }
    setSaving(true);
    try {
      const nextProposals = [
        ...(tag.lab_proposals || []),
        { lab: newLab, colors: newColors, clarities: newClarities },
      ];
      await axios.patch(
        `${API}/api/diamond-verification/${tag.id}`,
        { lab_proposals: nextProposals },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setTag(t => ({ ...t, lab_proposals: nextProposals }));
      setModalOpen(false);
      toast.success(`${newLab} lab proposal added.`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add lab');
    } finally {
      setSaving(false);
    }
  };

  // ── Reopen for Revision ────────────────────────────────────────────
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const revision = tag.revision || 1;

  const startReopen = async () => {
    setReopening(true);
    try {
      await axios.post(
        `${API}/api/diamond-verification/${tag.id}/reopen`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      // Backend approved — open the wizard prefilled with this tag's data.
      // The wizard sees `tag.status === 'complete'` and switches to revision mode.
      setReopenConfirmOpen(false);
      onReopen?.(tag);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cannot reopen this DV');
    } finally {
      setReopening(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50">

      {/* ── Compact header ──────────────────────────────────────────── */}
      <div className="bg-emerald-700 text-white shrink-0">
        <div className="flex items-center gap-3 px-4 pt-3 pb-2">
          <button onClick={onClose} className="p-2 rounded-full hover:bg-emerald-600 active:bg-emerald-800 shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-xl font-bold font-mono">#{tag.tag_number}</p>
              <span className="text-[11px] px-2.5 py-0.5 rounded-full font-bold bg-white text-emerald-700">
                ✓ Complete
              </span>
              {revision > 1 && (
                <span
                  className="text-[11px] px-2.5 py-0.5 rounded-full font-bold bg-amber-400 text-amber-900"
                  data-testid="revision-badge"
                  title={tag.parent_dv_id ? `Revision ${revision} of this tag` : ''}
                >
                  Rev {revision}
                </span>
              )}
              {tag.sell_mode === 'tds_interim' && tag.lab_status === 'in_lab' && (
                <span
                  className="text-[11px] px-2.5 py-0.5 rounded-full font-bold bg-sky-500 text-white"
                  data-testid="tds-interim-badge"
                  title="Lab submission parked — sellable at TDS pricing until lab results arrive"
                >
                  Sellable at TDS · Lab pending
                </span>
              )}
              {tag.actual?.data_source && (
                <span className="text-[11px] px-2.5 py-0.5 rounded-full font-bold bg-emerald-900/40 border border-emerald-300 text-white" data-testid={`detail-source-badge-${tag.actual.data_source}`}>
                  Source: {SOURCE_META[tag.actual.data_source]?.label || 'Manual'}
                </span>
              )}
            </div>
            <p className="text-xs text-emerald-200 mt-0.5">
              {[tag.jewellery_ref, tag.created_by_name,
                new Date(tag.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
              ].filter(Boolean).join(' · ')}
            </p>
          </div>
          {canReopen && (
            <button
              onClick={() => setReopenConfirmOpen(true)}
              data-testid="reopen-btn"
              className="p-2 rounded-full hover:bg-emerald-600 active:bg-emerald-800 shrink-0"
              title="Reopen this DV as a new revision"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Stat strip */}
        {(totalCts != null || totalPcs != null || grossWt != null) && (
          <div className="flex gap-px mx-4 mb-3 mt-1 rounded-xl overflow-hidden border border-emerald-600">
            {[
              { val: fmtCts(totalCts),  label: 'Total cts' },
              { val: totalPcs,          label: 'Total pcs' },
              { val: grossWt ? `${grossWt}g` : null, label: 'Gross wt' },
              { val: entries.length || null, label: 'Entries' },
            ].filter(x => x.val != null).map(({ val, label }) => (
              <div key={label} className="flex-1 bg-emerald-800/60 text-center py-2 px-1">
                <p className="text-sm font-bold text-white">{val}</p>
                <p className="text-[9px] text-emerald-300 font-semibold">{label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Body ────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">

        {/* Entries table */}
        {entries.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Diamond Entries</p>
              {entries.length > 1 && (
                <p className="text-xs text-emerald-700 font-bold">{fmtCts(totalCts)} cts · {totalPcs} pcs total</p>
              )}
            </div>
            <div className="divide-y divide-gray-50">
              {entries.map((e, idx) => {
                const isDiamond = isDiamondEntry(e);
                return (
                <div key={idx} className="px-4 py-3">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="text-[10px] font-bold text-emerald-700 uppercase w-12 shrink-0">
                      Entry {idx + 1}
                    </span>
                    {e.shape && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border shrink-0 ${
                        e.shape.toLowerCase() === 'round'
                          ? 'bg-sky-50 text-sky-700 border-sky-200'
                          : 'bg-violet-50 text-violet-700 border-violet-200'
                      }`}>{e.shape}</span>
                    )}
                    {!isDiamond && (
                      <span
                        className="text-[10px] px-2 py-0.5 rounded-full font-bold border bg-amber-50 text-amber-700 border-amber-200 shrink-0"
                        title="Not counted toward diamond totals"
                        data-testid={`entry-nondiamond-badge-${idx}`}
                      >
                        {e.is_silent ? 'Silent' : (e.is_diamond === false ? 'Non-diamond' : 'Excluded')}
                      </span>
                    )}
                    {e.colors?.length > 0 && (
                      <span className="text-xs font-bold text-gray-800">{e.colors.join(', ')}</span>
                    )}
                    {e.clarities?.length > 0 && (
                      <>
                        <span className="text-gray-300 text-xs">·</span>
                        <span className="text-xs font-bold text-gray-600">{e.clarities.join(', ')}</span>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2 ml-14">
                    <div className="flex items-baseline gap-1">
                      <span className="text-sm font-bold text-gray-900">{e.weight_cts ?? '—'}</span>
                      <span className="text-[10px] text-gray-400">cts</span>
                      {isDiamond && pct(e.weight_cts, totalCts) && (
                        <span className="text-[10px] font-bold text-emerald-600 ml-0.5">{pct(e.weight_cts, totalCts)}</span>
                      )}
                    </div>
                    <span className="text-gray-200 text-sm">|</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-sm font-bold text-gray-900">{e.count ?? '—'}</span>
                      <span className="text-[10px] text-gray-400">pcs</span>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Colour / Clarity breakdown */}
            {showBreakdown && (
              <div className="border-t border-gray-100 px-4 py-3">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2">Breakdown</p>
                <div className="space-y-2">
                  <div>
                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">By Colour</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {colourBreakdown.map(({ label, cts }) => (
                        <div key={label} className="flex items-baseline gap-1">
                          <span className="text-xs font-bold text-gray-800">{label}</span>
                          <span className="text-[10px] font-bold text-emerald-600">{pct(cts, totalCts)}</span>
                          <span className="text-[9px] text-gray-400">cts</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {clarityBreakdown.length > 1 ? (
                    <div>
                      <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">By Clarity</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {clarityBreakdown.map(({ label, cts }) => (
                          <div key={label} className="flex items-baseline gap-1">
                            <span className="text-xs font-bold text-gray-800">{label}</span>
                            <span className="text-[10px] font-bold text-emerald-600">{pct(cts, totalCts)}</span>
                            <span className="text-[9px] text-gray-400">cts</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : clarityBreakdown.length === 1 && (
                    <div className="flex items-baseline gap-1.5">
                      <p className="text-[9px] font-bold text-gray-400 uppercase">Clarity</p>
                      <span className="text-xs font-bold text-gray-800">{clarityBreakdown[0].label}</span>
                      <span className="text-[10px] font-bold text-emerald-600">{pct(clarityBreakdown[0].cts, totalCts)}</span>
                      <span className="text-[9px] text-gray-400">cts</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* TDS Visibility */}
        {tag.tds && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-100">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">TDS Visibility</p>
            </div>
            <div className="px-4 py-3 space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-[10px] font-bold text-gray-400 uppercase w-12 shrink-0">Colour</span>
                <span className="text-gray-600 font-mono">{allActColors.join(', ') || '—'}</span>
                <span className="text-gray-300">→</span>
                <span className={`font-mono font-bold ${colourMatch ? 'text-emerald-600' : 'text-red-500'}`}>
                  {tdsColors.join(', ') || '—'}
                </span>
                <span className="ml-auto">{colourMatch ? '✓' : '⚠'}</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-[10px] font-bold text-gray-400 uppercase w-12 shrink-0">Clarity</span>
                <span className="text-gray-600 font-mono">{allActClarities.join(', ') || '—'}</span>
                <span className="text-gray-300">→</span>
                <span className={`font-mono font-bold ${clarityMatch ? 'text-emerald-600' : 'text-red-500'}`}>
                  {tdsClarities.join(', ') || '—'}
                </span>
                <span className="ml-auto">{clarityMatch ? '✓' : '⚠'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Lab proposals */}
        {((tag.lab_proposals || []).length > 0 || (canAddLab && availableLabs.length > 0)) && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Lab Proposals</p>
              {canAddLab && availableLabs.length > 0 && (
                <button
                  onClick={openAddLab}
                  data-testid="add-lab-btn"
                  className="flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:text-emerald-800 active:text-emerald-900"
                >
                  <Plus className="w-3.5 h-3.5" /> Add Lab
                </button>
              )}
            </div>
            <div className="divide-y divide-gray-50">
              {(tag.lab_proposals || []).map(p => (
                <div key={p.lab} className="flex items-center gap-3 px-4 py-3">
                  <span className={`text-xs px-2.5 py-1 rounded-lg text-white font-bold shrink-0 ${LAB_COLORS[p.lab]}`}>
                    {p.lab}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(p.colors || []).map(c => (
                        <span key={c} className="text-[11px] font-bold text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">{c}</span>
                      ))}
                      {p.colors?.length > 0 && p.clarities?.length > 0 && (
                        <span className="text-gray-300 text-xs">·</span>
                      )}
                      {(p.clarities || []).map(c => (
                        <span key={c} className="text-[11px] font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">{c}</span>
                      ))}
                      {!p.colors?.length && !p.clarities?.length && (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="h-4" />
      </div>

      {/* ── Reopen confirm modal ──────────────────────────────────── */}
      {reopenConfirmOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/40" data-testid="reopen-modal">
          <div className="bg-white rounded-t-3xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-bold text-lg">Reopen for Revision?</p>
              <button
                onClick={() => setReopenConfirmOpen(false)}
                data-testid="reopen-close-btn"
                className="p-1 rounded-full hover:bg-gray-100"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              This creates a fresh Revision {revision + 1} of <span className="font-mono font-bold">#{tag.tag_number}</span>.
              The current version stays intact for audit but is marked <span className="font-bold text-amber-700">superseded</span> the
              moment your team submits the new one.
            </p>
            <p className="text-xs text-gray-500 mb-1">
              • The wizard opens EMPTY and pulls the latest stones &amp; weights from ERP automatically.
            </p>
            <p className="text-xs text-gray-500 mb-1">
              • Your usual DV team (Prod Supervisor / Diamond Dept) does the re-verification from here.
            </p>
            <p className="text-xs text-gray-500 mb-4">
              • Blocked automatically if the tag is currently at a lab (batch dispatched).
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setReopenConfirmOpen(false)}
                data-testid="reopen-cancel-btn"
                className="flex-1 py-3 rounded-2xl border border-gray-200 text-gray-600 font-bold text-sm"
              >
                Cancel
              </button>
              <button
                onClick={startReopen}
                disabled={reopening}
                data-testid="reopen-confirm-btn"
                className="flex-1 py-3 bg-emerald-600 text-white font-bold rounded-2xl disabled:opacity-50 active:bg-emerald-700"
              >
                {reopening ? 'Checking…' : `Start Revision ${revision + 1}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Lab modal ──────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/40" data-testid="add-lab-modal">
          <div className="bg-white rounded-t-3xl p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-lg">Add Lab Proposal</p>
              <button
                onClick={() => setModalOpen(false)}
                data-testid="add-lab-close-btn"
                className="p-1 rounded-full hover:bg-gray-100"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Lab picker */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Lab</p>
                <div className="flex gap-2">
                  {availableLabs.map(lab => (
                    <button
                      key={lab}
                      onClick={() => setNewLab(lab)}
                      data-testid={`add-lab-pick-${lab}`}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                        newLab === lab
                          ? `${LAB_COLORS[lab]} text-white`
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {lab}
                    </button>
                  ))}
                </div>
              </div>

              {/* Colour picker (clamped) */}
              <ColorPicker
                selected={newColors}
                onChange={setNewColors}
                label="Proposed Colour"
                compact
                disabledBeyond={worstActualColor}
              />

              {/* Clarity picker (clamped) */}
              <ClarityPicker
                selected={newClarities}
                onChange={setNewClarities}
                label="Proposed Clarity"
                compact
                disabledBeyond={worstActualClarity}
              />

              <p className="text-[11px] text-gray-500 leading-relaxed">
                This adds a new lab grade to the existing DV. The tag becomes eligible
                for the next {newLab || 'lab'} batch. The rest of the DV stays untouched.
              </p>

              <button
                onClick={saveAddLab}
                disabled={saving || !newLab || !newColors.length || !newClarities.length}
                data-testid="add-lab-save-btn"
                className="w-full py-3 bg-emerald-600 text-white font-bold rounded-2xl disabled:opacity-50 active:bg-emerald-700"
              >
                {saving ? 'Saving…' : 'Add Lab Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
